import { useState } from 'react'
import { useLayoutStore } from '../../stores/layoutStore'

export default function LayoutOptions() {
  const [isOpen, setIsOpen] = useState(false)
  const {
    pageSize,
    cardsPerPage,
    includeBleed,
    includeCutLines,
    frontsOnly,
    cutLineColor,
    cutLineStyle,
    cutLineWidth,
    cutLineOverCards,
    maxPdfMb,
    setPageSize,
    setCardsPerPage,
    setIncludeBleed,
    setIncludeCutLines,
    setFrontsOnly,
    setCutLineColor,
    setCutLineStyle,
    setCutLineWidth,
    setCutLineOverCards,
    setMaxPdfMb,
  } = useLayoutStore()

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header with toggle */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
      >
        <h3 className="font-semibold text-lg">Layout Options</h3>
        <span className="text-gray-400 text-xl leading-none">{isOpen ? '−' : '+'}</span>
      </button>

      {/* Collapsible content */}
      {isOpen && (
        <div className="p-4 pt-0 space-y-4 border-t border-gray-100">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Page Size
            </label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value as 'a4' | 'letter')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="a4">A4</option>
              <option value="letter">Letter</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cards per Page
            </label>
            <select
              value={cardsPerPage}
              onChange={(e) => setCardsPerPage(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={9}>9 (3×3)</option>
              <option value={6}>6 (2×3)</option>
              <option value={4}>4 (2×2)</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="bleed"
              checked={includeBleed}
              onChange={(e) => setIncludeBleed(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label htmlFor="bleed" className="text-sm text-gray-700">
              Include bleed margin
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="fronts-only"
              checked={frontsOnly}
              onChange={(e) => setFrontsOnly(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label htmlFor="fronts-only" className="text-sm text-gray-700">
              Solo frontales (sin páginas de trasera)
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="cutlines"
              checked={includeCutLines}
              onChange={(e) => setIncludeCutLines(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <label htmlFor="cutlines" className="text-sm text-gray-700">
              Include cut lines
            </label>
          </div>

          {includeCutLines && (
            <div className="space-y-3 pl-3 border-l-2 border-gray-200">
              <div className="flex items-center gap-3">
                <label
                  htmlFor="cut-color"
                  className="text-sm font-medium text-gray-700 w-20"
                >
                  Color
                </label>
                <input
                  type="color"
                  id="cut-color"
                  value={cutLineColor}
                  onChange={(e) => setCutLineColor(e.target.value)}
                  className="h-8 w-12 cursor-pointer rounded border border-gray-300"
                />
                <span className="text-xs text-gray-500">{cutLineColor}</span>
              </div>

              <div className="flex items-center gap-3">
                <label
                  htmlFor="cut-width"
                  className="text-sm font-medium text-gray-700 w-20"
                >
                  Grosor (pt)
                </label>
                <input
                  type="number"
                  id="cut-width"
                  min={0.1}
                  max={10}
                  step={0.1}
                  value={cutLineWidth}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value)
                    if (!Number.isNaN(v)) {
                      setCutLineWidth(Math.min(10, Math.max(0.1, v)))
                    }
                  }}
                  className="w-24 px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700 mb-1">
                  Estilo
                </span>
                <div className="space-y-1">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="cut-style"
                      checked={cutLineStyle === 'ticks'}
                      onChange={() => setCutLineStyle('ticks')}
                      className="text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    Marcas de margen (para copistería)
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="cut-style"
                      checked={cutLineStyle === 'full'}
                      onChange={() => setCutLineStyle('full')}
                      className="text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    Líneas completas de lado a lado (para autocorte)
                  </label>
                </div>
              </div>

              {cutLineStyle === 'full' && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="cut-over"
                    checked={cutLineOverCards}
                    onChange={(e) => setCutLineOverCards(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="cut-over" className="text-sm text-gray-700">
                    Dibujar las líneas sobre las cartas
                  </label>
                </div>
              )}
            </div>
          )}

          <div>
            <label
              htmlFor="max-pdf-mb"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Tamaño máximo de PDF (MB)
            </label>
            <input
              type="number"
              id="max-pdf-mb"
              min={1}
              max={2500}
              step={1}
              value={maxPdfMb ?? ''}
              placeholder="Sin límite"
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '') {
                  setMaxPdfMb(null)
                  return
                }
                const v = parseFloat(raw)
                if (!Number.isNaN(v)) {
                  setMaxPdfMb(Math.min(2500, Math.max(1, v)))
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Si el PDF supera este tamaño estimado, se divide en varios archivos
              dentro de un ZIP. Vacío = un solo PDF.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
